import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COL } from '@eprintsmart/shared';
import { db } from '../firebase';

/**
 * A light CRM: contacts (leads and customers) with a stage and a timeline of
 * interactions. Deliberately provider-agnostic on messaging — WhatsApp goes out
 * through a Cloud Function whose adapter is MSG91, so switching or adding a
 * channel never touches this UI.
 */

export const CRM_STAGES = ['NEW', 'CONTACTED', 'QUOTED', 'WON', 'LOST'] as const;
export type CrmStage = (typeof CRM_STAGES)[number];

export const STAGE_LABEL: Record<CrmStage, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUOTED: 'Quoted',
  WON: 'Won',
  LOST: 'Lost',
};

export interface CrmContact {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  company?: string;
  stage: CrmStage;
  tags: string[];
  source?: string;
  note?: string;
  createdAt: number;
  updatedAt: number;
}

export type InteractionType = 'note' | 'whatsapp' | 'call' | 'email';

export interface CrmInteraction {
  id: string;
  type: InteractionType;
  body: string;
  /** out = we contacted them, in = they contacted us, internal = a private note. */
  direction: 'out' | 'in' | 'internal';
  at: number;
  by?: string;
}

const contacts = () => collection(db, COL.crmContacts);
const interactions = (contactId: string) => collection(db, `${COL.crmContacts}/${contactId}/interactions`);

const fns = () => getFunctions(undefined, 'asia-south1');

export function watchContacts(cb: (rows: CrmContact[]) => void): () => void {
  const q = query(contacts(), orderBy('updatedAt', 'desc'));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ ...(d.data() as CrmContact), id: d.id }))));
}

export async function createContact(input: Omit<CrmContact, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const now = Date.now();
  const ref = doc(contacts());
  await setDoc(ref, { ...input, id: ref.id, createdAt: now, updatedAt: now });
  return ref.id;
}

export async function updateContact(id: string, patch: Partial<CrmContact>): Promise<void> {
  await updateDoc(doc(contacts(), id), { ...patch, updatedAt: Date.now() });
}

export function watchInteractions(contactId: string, cb: (rows: CrmInteraction[]) => void): () => void {
  const q = query(interactions(contactId), orderBy('at', 'desc'));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ ...(d.data() as CrmInteraction), id: d.id }))));
}

export async function addInteraction(
  contactId: string,
  input: Omit<CrmInteraction, 'id' | 'at'>,
): Promise<void> {
  await addDoc(interactions(contactId), { ...input, at: Date.now(), createdAt: serverTimestamp() });
  await updateContact(contactId, {});
}

/** Sends a WhatsApp message via the MSG91-backed Cloud Function, which logs it
 * onto the contact's timeline. Throws with a human message when the provider is
 * not yet configured, so the UI can surface "add your MSG91 key". */
export async function sendWhatsApp(payload: { contactId: string; message: string }): Promise<{ ok: boolean }> {
  const call = httpsCallable<typeof payload, { ok: boolean }>(fns(), 'sendWhatsApp');
  return (await call(payload)).data;
}
